// src/Pages/TermsOfServicePage.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0118] via-[#1a0f2e] to-[#0a0118] text-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Terms of Service
            </h1>
            <p className="text-slate-400 mt-1">Last Updated: January 9, 2026</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert max-w-none space-y-8">
          
          {/* Welcome */}
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-2xl font-bold text-purple-300 mb-4">Welcome to WHIPT</h2>
            <p className="text-slate-300 leading-relaxed">
              These Terms of Service ("Terms") govern your access to and use of WHIPT (Where'd I Put That?), 
              an academic organization and wellness platform operated by Pronoia Wellness. By accessing or using 
              WHIPT, you agree to be bound by these Terms.
            </p>
          </div>

          {/* 1. Acceptance of Terms */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              By creating an account or using WHIPT, you acknowledge that you have read, understood, and agree 
              to be bound by these Terms and our Privacy Policy. If you do not agree, please do not use our services.
            </p>
          </section>

          {/* 2. Age Requirements */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Age Requirements & Parental Consent</h2>
            <div className="space-y-4 text-slate-300">
              <p className="leading-relaxed">
                <strong className="text-purple-300">Under 13 Years:</strong> Users under 13 require verifiable 
                parental consent (COPPA compliance). Limited features available. Parent/guardian email required.
              </p>
              <p className="leading-relaxed">
                <strong className="text-purple-300">Ages 13-17 (High School):</strong> Age-appropriate content 
                and features. Messaging, music, and video rooms available with safety features enabled.
              </p>
              <p className="leading-relaxed">
                <strong className="text-purple-300">Ages 18+ (College/University):</strong> Full access to all 
                features and functionality.
              </p>
            </div>
          </section>

          {/* 3. Account Responsibilities */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Account Responsibilities</h2>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>You must notify us immediately of any unauthorized account access</li>
              <li>One account per person; account sharing is prohibited</li>
              <li>You are responsible for all activities that occur under your account</li>
            </ul>
          </section>

          {/* 4. Acceptable Use */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Acceptable Use Policy</h2>
            <p className="text-slate-300 leading-relaxed mb-4">You agree NOT to:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>Use WHIPT for any illegal or unauthorized purpose</li>
              <li>Harass, bully, or harm other users</li>
              <li>Share inappropriate, offensive, or harmful content</li>
              <li>Impersonate others or create fake accounts</li>
              <li>Attempt to hack, disrupt, or damage the platform</li>
              <li>Scrape, copy, or misuse platform content or data</li>
              <li>Spam or send unsolicited messages to users</li>
              <li>Share academic work dishonestly (plagiarism, cheating)</li>
            </ul>
          </section>

          {/* 5. Content Ownership */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Content Ownership & License</h2>
            <div className="space-y-4 text-slate-300">
              <p className="leading-relaxed">
                <strong className="text-purple-300">Your Content:</strong> You retain ownership of all content 
                you create (notes, documents, journal entries, etc.). By using WHIPT, you grant us a limited 
                license to store, display, and process your content solely to provide our services.
              </p>
              <p className="leading-relaxed">
                <strong className="text-purple-300">Platform Content:</strong> All WHIPT software, design, 
                features, and branding are owned by Pronoia Wellness and protected by intellectual property laws.
              </p>
            </div>
          </section>

          {/* 6. Privacy & Data */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Privacy & Data Protection</h2>
            <p className="text-slate-300 leading-relaxed">
              Your privacy is important to us. Please review our Privacy Policy to understand how we collect, 
              use, and protect your information. We comply with COPPA, FERPA, and applicable data protection 
              regulations.
            </p>
          </section>

          {/* 7. Educational Use */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Educational Use & Academic Integrity</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              WHIPT is designed to support legitimate academic work. You agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>Use AI features ethically and in accordance with your institution's policies</li>
              <li>Properly cite sources and maintain academic integrity</li>
              <li>Not use WHIPT to facilitate academic dishonesty</li>
              <li>Comply with your educational institution's technology policies</li>
            </ul>
          </section>

          {/* 8. Service Availability */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Service Availability</h2>
            <p className="text-slate-300 leading-relaxed">
              We strive to provide reliable service but do not guarantee uninterrupted access. We may modify, 
              suspend, or discontinue features at any time. We are not liable for data loss; please maintain 
              backups of important content.
            </p>
          </section>

          {/* 9. Termination */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Account Termination</h2>
            <div className="space-y-4 text-slate-300">
              <p className="leading-relaxed">
                <strong className="text-purple-300">By You:</strong> You may delete your account at any time 
                through Settings. Data deletion is permanent and cannot be undone.
              </p>
              <p className="leading-relaxed">
                <strong className="text-purple-300">By Us:</strong> We may suspend or terminate accounts that 
                violate these Terms, engage in harmful behavior, or misuse the platform.
              </p>
            </div>
          </section>

          {/* 10. Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Disclaimer of Warranties</h2>
            <p className="text-slate-300 leading-relaxed">
              WHIPT is provided "as is" without warranties of any kind. We do not guarantee that the service will 
              be error-free, secure, or meet your specific requirements. AI features are tools to assist learning 
              and should not replace critical thinking or original work.
            </p>
          </section>

          {/* 11. Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Limitation of Liability</h2>
            <p className="text-slate-300 leading-relaxed">
              To the maximum extent permitted by law, Pronoia Wellness shall not be liable for any indirect, 
              incidental, special, or consequential damages arising from your use of WHIPT, including data loss, 
              academic consequences, or service interruptions.
            </p>
          </section>

          {/* 12. Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Changes to Terms</h2>
            <p className="text-slate-300 leading-relaxed">
              We may update these Terms from time to time. We will notify you of significant changes via email 
              or in-app notification. Continued use after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* 13. Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. Governing Law</h2>
            <p className="text-slate-300 leading-relaxed">
              These Terms are governed by the laws of the United States. Any disputes shall be resolved through 
              binding arbitration or in the courts of [Your Jurisdiction].
            </p>
          </section>

          {/* Contact */}
          <section className="bg-purple-900/20 rounded-2xl p-6 border border-purple-400/30">
            <h2 className="text-2xl font-bold text-white mb-4">14. Contact Us</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Questions about these Terms? Contact us:
            </p>
            <ul className="space-y-2 text-slate-300">
              <li><strong className="text-purple-300">Email:</strong> legal@theblackbettermentquest.org</li>
              <li><strong className="text-purple-300">Support:</strong> support@theblackbettermentquest.org</li>
              <li><strong className="text-purple-300">Website:</strong> www.theblackbettermentquest.org</li>
            </ul>
          </section>

          {/* Agreement */}
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-6 border border-purple-400/30">
            <p className="text-white font-semibold text-center">
              By using WHIPT, you acknowledge that you have read, understood, and agree to these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
